import type { Feature, LineString, Position } from 'geojson';
import type { LatLngElevPoint } from './types/engineTypes';
import type { RacemapShadowtrackFeatureCollection } from './types/geos';
/* eslint-disable camelcase */
import type { LatLng } from './types/trackping';

// using turf earth radius
// https://github.com/Turfjs/turf/blob/9f151a503c9679c25b516137034f3ebfc6271d9f/packages/turf-helpers/index.ts#L39
export const Degrees2radians = Math.PI / 180;
const EarthRadiusInMeter = 6371008.8; // 6378137 // 6373000
const EarthDiameterInMeter = 2 * EarthRadiusInMeter;

/*
 * This function calculates distance of two points on a sphere
 */
export function calculateDistanceInMeter(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const dLat = 0.5 * (lat2 - lat1) * Degrees2radians;
  const dLon = 0.5 * (lon2 - lon1) * Degrees2radians;
  const sinDLat = Math.sin(dLat);
  const sinDLon = Math.sin(dLon);
  const a =
    sinDLat * sinDLat +
    Math.cos(lat1 * Degrees2radians) * Math.cos(lat2 * Degrees2radians) * sinDLon * sinDLon;
  return EarthDiameterInMeter * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * calculates the delta Latitude in degree by a given distance in meters
 * Keep in mind Latitude does not depend on Longitude
 * Lat
 *  ^
 *  |
 *  |
 *  |
 *  |----------> Lng
 *
 * @param distance in meters
 */
export function distToDeltaLat(distance: number): number {
  return distance / calculateDistanceInMeter(0, 0, 1, 0);
}

// chunks an array of points
// Position = [lng, lat, elv]
// raw:   * *  * *   *              *  *        *
// chunk: |       |       |       |       |      |
export function chunkLineString(
  lineString: Feature<LineString>,
  chunkLength = 10,
): Array<Position> {
  const coords = lineString.geometry.coordinates;
  const result: Array<Position> = [];
  if (coords.length > 0) {
    let currentLength = 0;
    let chunkCount = 1;
    result.push(coords[0]);
    for (let a = 1; a < coords.length; a++) {
      const p0 = coords[a - 1];
      const p1 = coords[a];
      const distance = calculateDistanceInMeter(p0[1], p0[0], p1[1], p1[0]);
      // we are always multiplying preventing sum up errors
      while (currentLength + distance > chunkCount * chunkLength) {
        // we are to far and have to calculate points behind us to match the chunkLength pattern until chunkedTracklength + chunkLength > currentLength
        const scale = (chunkCount * chunkLength - currentLength) / distance;
        result.push([p0[0] + scale * (p1[0] - p0[0]), p0[1] + scale * (p1[1] - p0[1])]);
        chunkCount += 1;
      }
      currentLength += distance;
    }
  }
  return result;
}

/**
 * calculates the delta Longitude in degree by a given distance in meters at a given point of Latitude
 * Keep in mind Longitude strongly depends your current Latitude
 * Lat
 *  ^
 *  |
 *  |
 *  |
 *  |----------> Lng
 *
 * @param distance in meters
 */
export function distToDeltaLng(distance: number, lat: number): number {
  return distance / calculateDistanceInMeter(lat, 0, lat, 1);
}

/*
 * This function interpolates between two real numbers (a, b) by an interpolation factor t (a, b, t € R)
 * Test exists
 */
function interpolate(a: number, b: number, t: number): number {
  return a + t * (b - a);
}

function interpolateBetweenTwoLatLngElevPoints(
  pA: LatLngElevPoint,
  pB: LatLngElevPoint,
  t: number,
): LatLngElevPoint {
  return {
    lng: interpolate(pA.lng, pB.lng, t),
    lat: interpolate(pA.lat, pB.lat, t),
    elv: interpolate(pA.elv, pB.elv, t),
  };
}

export function addRandomLatDeviation(lat: number, deviationLimit: number): number {
  return lat + distToDeltaLat(deviationLimit * 2 * (Math.random() - 1));
}

export function addRandomLngDeviation(lng: number, latRef: number, deviationLimit: number): number {
  return lng + distToDeltaLng(deviationLimit * 2 * (Math.random() - 1), latRef);
}

export function addRandomLocationDeviation(location: LatLng, deviationLimit: number): LatLng {
  return [
    addRandomLatDeviation(location[1], deviationLimit),
    addRandomLngDeviation(location[0], location[1], deviationLimit),
  ];
}

/*
 * This function find tha point on the track, that has a target offset in meter. If the offset lay between two points,
 * it give the interpolated point back.
 */
export function findPositionOnTrackByOffset(
  offsetInMeter: number,
  unchunkedTrack: Array<Position>,
): { lat?: number; lng?: number; elv?: number; pointIndex: number } | undefined {
  let pointIndex: number;
  let lastOffset = 0;
  let lengthTotal = 0;

  for (pointIndex = 1; pointIndex < unchunkedTrack.length; pointIndex++) {
    const distanceToPrevPoint = calculateDistanceInMeter(
      unchunkedTrack[pointIndex][1],
      unchunkedTrack[pointIndex][0],
      unchunkedTrack[pointIndex - 1][1],
      unchunkedTrack[pointIndex - 1][0],
    );
    lengthTotal = lengthTotal + distanceToPrevPoint;

    if (offsetInMeter === lengthTotal) return { pointIndex };
    if (offsetInMeter < lengthTotal) {
      const point = unchunkedTrack[pointIndex];
      const lastPoint = unchunkedTrack[pointIndex - 1];

      const pA: LatLngElevPoint = { lng: lastPoint[0], lat: lastPoint[1], elv: lastPoint[2] };
      const pB: LatLngElevPoint = { lng: point[0], lat: point[1], elv: point[2] };
      const t = (offsetInMeter - lastOffset) / (lengthTotal - lastOffset);

      const interpolatedPoint = interpolateBetweenTwoLatLngElevPoints(pA, pB, t);
      return {
        lat: interpolatedPoint.lat,
        lng: interpolatedPoint.lng,
        elv: interpolatedPoint.elv,
        pointIndex,
      };
    }
    lastOffset = lengthTotal;
  }

  return;
}

/*
 * This function find tha point on the track, that has a target offset in meter. If the offset lay between two points,
 * it give the interpolated point back.
 */

type PositionOnTrackByOffset = { coordinates: LatLng; coordinateIndex: number; offset: number };

export function findNearestPointAndIndexOnShadowTrack(
  shadowTrack: RacemapShadowtrackFeatureCollection,
  offsetInMeter: number,
): PositionOnTrackByOffset {
  const result: PositionOnTrackByOffset = {
    coordinates: [0.0, 0.0],
    coordinateIndex: -1,
    offset: 0,
  };

  if (
    shadowTrack.features.length > 0 &&
    shadowTrack.features[0]?.geometry != null &&
    Array.isArray(shadowTrack.features[0]?.geometry?.coordinates)
  ) {
    const unchunkedTrack: Array<LatLng> = shadowTrack.features[0].geometry.coordinates.map(
      (position: Array<number>) => {
        return [position[0], position[1]];
      },
    );

    let coordinateIndex: number;
    let lastOffset = 0;
    let lengthTotal = 0;

    for (coordinateIndex = 1; coordinateIndex < unchunkedTrack.length; coordinateIndex++) {
      const distanceToPrevPoint = calculateDistanceInMeter(
        unchunkedTrack[coordinateIndex][1],
        unchunkedTrack[coordinateIndex][0],
        unchunkedTrack[coordinateIndex - 1][1],
        unchunkedTrack[coordinateIndex - 1][0],
      );
      lengthTotal = lengthTotal + distanceToPrevPoint;

      if (offsetInMeter === lengthTotal)
        return {
          coordinateIndex,
          coordinates: unchunkedTrack[coordinateIndex],
          offset: lengthTotal,
        };
      if (offsetInMeter < lengthTotal) {
        const point = unchunkedTrack[coordinateIndex];
        const lastPoint = unchunkedTrack[coordinateIndex - 1];

        const t = (offsetInMeter - lastOffset) / (lengthTotal - lastOffset);

        if (t < 0.5) {
          return {
            coordinates: lastPoint,
            coordinateIndex: coordinateIndex - 1,
            offset: lengthTotal - distanceToPrevPoint,
          };
        }
        return {
          coordinates: point,
          coordinateIndex: coordinateIndex,
          offset: lengthTotal,
        };
      }
      lastOffset = lengthTotal;
    }
  }

  return result;
}
